import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, ThumbsUp, Eye, MessageSquare, Share2, Clock, TrendingUp, ArrowUpRight, ArrowDownRight, Globe } from "lucide-react";
import { fetchSocialPosts, fetchSocialChart, fetchSocialStats, SOCIAL_CATEGORIES, SOCIAL_CAT_COLORS } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TABS = [
  { id: "reddit", label: "Reddit", color: "#FF4500" },
  { id: "tiktok", label: "TikTok", color: "#00F2EA" },
  { id: "news", label: "News Alerts", color: "#FFEE2F" },
];

const PERIODS = [
  { value: '24h', label: '24H' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

const CAT_ENTRIES = [{ key: 'all', name: 'All' }, ...Object.entries(SOCIAL_CATEGORIES).map(([key, name]) => ({ key, name }))];

export default function SocialPage() {
  const [platform, setPlatform] = useState("reddit");
  const [category, setCategory] = useState("all");
  const [period, setPeriod] = useState("30d");
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [chart, setChart] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { platform, period, page, limit: 40 };
      if (category !== 'all') params.category = category;
      const data = await fetchSocialPosts(params);
      setPosts(data.posts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [platform, category, period, page]);

  const loadChart = useCallback(async () => {
    try {
      const params = { period };
      if (category !== 'all') params.category = category;
      const data = await fetchSocialChart(params);
      setChart(data.chart || []);
    } catch (e) { console.error(e); }
  }, [category, period]);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchSocialStats();
      setStats(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPage(1); }, [platform, category, period]);
  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { loadChart(); }, [loadChart]);

  const platformColor = platform === 'reddit' ? '#FF4500' : '#00F2EA';

  return (
    <div className="space-y-6" data-testid="social-page">
      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="social-stats">
          <StatCard label="Reddit Posts" value={stats.reddit_total} color="#FF4500" />
          <StatCard label="TikTok Videos" value={stats.tiktok_total} color="#00F2EA" />
          <StatCard label="News Alerts" value={stats.news_total || 0} color="#FFEE2F" />
          <StatCard label="Last 24h" value={stats.recent_24h} color="#10B981" />
          <StatCard label="Sentiment" value={`${stats.sentiment?.positive || 0} pos / ${stats.sentiment?.negative || 0} neg`} color="#8B5CF6" small />
        </div>
      )}

      {/* Platform Tabs + Period Filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 border-b border-gray-200 pb-0">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setPlatform(tab.id)}
              className={`px-6 py-3 font-display text-sm font-bold uppercase tracking-wider transition-all duration-200 border-b-2 ${
                platform === tab.id ? 'text-white border-current' : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
              style={platform === tab.id ? { color: tab.color, borderColor: tab.color } : {}}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                period === p.value ? 'text-white border-current' : 'text-gray-400 border-gray-200 hover:text-gray-800'
              }`}
              style={period === p.value ? { color: platformColor, borderColor: platformColor, background: `${platformColor}12` } : {}}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        {CAT_ENTRIES.map(({ key, name }) => (
          <button
            key={key}
            onClick={() => setCategory(key)}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border flex items-center gap-2 ${
              category === key ? "text-white" : "bg-transparent border-gray-200 text-gray-500 hover:text-gray-800"
            }`}
            style={category === key ? {
              background: `${SOCIAL_CAT_COLORS[key] || '#fff'}15`,
              borderColor: `${SOCIAL_CAT_COLORS[key] || '#fff'}40`,
              color: key === 'all' ? '#fff' : SOCIAL_CAT_COLORS[key]
            } : {}}
            data-testid={`cat-${key}`}
          >
            {key !== 'all' && <span className="w-2 h-2 rounded-sm" style={{ background: SOCIAL_CAT_COLORS[key] }} />}
            {name}
          </button>
        ))}
      </div>

      {/* Mentions Volume Chart */}
      {chart.length > 0 && (
        <Card className="glass-card border-gray-200 rounded-none" data-testid="volume-chart">
          <CardHeader className="pb-2 border-b border-gray-200">
            <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
              Mentions Volume
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 pb-2">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'Inter, Arial, sans-serif' }}
                  tickFormatter={d => { const dt = new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 0, fontFamily: 'Inter, Arial, sans-serif', fontSize: 11 }}
                  labelStyle={{ color: '#6B7280' }}
                />
                <Bar dataKey="reddit" fill="#FF4500" opacity={0.8} />
                <Bar dataKey="tiktok" fill="#00F2EA" opacity={0.8} />
                <Bar dataKey="news" fill="#FFEE2F" opacity={0.8} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Posts Feed */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="posts-feed">
        <CardHeader className="pb-2 border-b border-gray-200 flex flex-row items-center justify-between">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: platformColor }} />
            {platform === 'reddit' ? 'Reddit Posts' : platform === 'tiktok' ? 'TikTok Videos' : 'News Alerts'}
            <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-gray-100 text-gray-600">
              {total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2 py-4">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-gray-100 animate-pulse" />)}</div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center">
              <Clock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="font-mono-data text-sm text-gray-400">No posts yet. Data is fetched every 6 hours.</p>
              <p className="font-mono-data text-xs text-gray-400 mt-1">First fetch may take a few minutes after startup</p>
            </div>
          ) : (
            <>
              {posts.map((post, i) => (
                <PostItem key={post.post_id || i} post={post} platform={platform} index={i} />
              ))}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-200">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="prev-page">Prev</button>
                  <span className="font-mono-data text-xs text-gray-500">{page} / {pages}</span>
                  <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="next-page">Next</button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, color, small }) {
  return (
    <Card className="bg-white border-gray-200 rounded-none shadow-sm">
      <CardContent className="py-3 px-4">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className={`font-bold ${small ? 'text-xs' : 'text-lg'}`} style={{ color }}>{value}</p>
      </CardContent>
    </Card>
  );
}

function PostItem({ post, platform, index }) {
  const catColor = SOCIAL_CAT_COLORS[post.category] || '#6B7280';
  const sentimentColor = post.sentiment_label === 'positive' ? '#10B981' : post.sentiment_label === 'negative' ? '#EF4444' : '#6B7280';

  return (
    <div className="feed-item flex items-start gap-3 animate-fade-in-up" style={{ animationDelay: `${index * 15}ms` }} data-testid={`post-${index}`}>
      {/* Score/Views */}
      <div className="flex flex-col items-center flex-shrink-0 w-14 text-center pt-0.5">
        {platform === 'reddit' ? (
          <>
            <ThumbsUp className="w-3 h-3 text-[#FF4500] mb-0.5" />
            <span className="font-mono-data text-xs font-bold text-gray-800">{post.upvotes || 0}</span>
          </>
        ) : platform === 'tiktok' ? (
          <>
            <Eye className="w-3 h-3 text-[#00F2EA] mb-0.5" />
            <span className="font-mono-data text-xs font-bold text-gray-800">{fmtNum(post.views)}</span>
          </>
        ) : (
          <>
            <Globe className="w-3 h-3 text-[#FFEE2F] mb-0.5" />
            <span className="font-mono-data text-[9px] font-bold text-gray-600">NEWS</span>
          </>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-800 hover:text-white transition-colors flex items-start gap-1.5">
          <span className="line-clamp-2">{post.title || 'Untitled'}</span>
          <ExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400 mt-0.5" />
        </a>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {platform === 'reddit' && post.subreddit && (
            <span className="font-mono-data text-[10px] text-[#FF4500]">r/{post.subreddit}</span>
          )}
          {platform === 'tiktok' && post.author && (
            <span className="font-mono-data text-[10px] text-[#00F2EA]">@{post.author}</span>
          )}
          {platform === 'news' && post.source && (
            <span className="font-mono-data text-[10px] text-[#FFEE2F]">{post.source}</span>
          )}
          <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
            <MessageSquare className="w-2.5 h-2.5" /> {post.comment_count || 0}
          </span>
          {platform === 'tiktok' && (
            <>
              <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
                <ThumbsUp className="w-2.5 h-2.5" /> {fmtNum(post.likes)}
              </span>
              <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
                <Share2 className="w-2.5 h-2.5" /> {fmtNum(post.shares)}
              </span>
            </>
          )}
          {/* Category badge */}
          <span className="inline-flex items-center gap-1 px-1.5 py-0 text-[9px] font-mono-data" style={{ background: `${catColor}15`, color: catColor, border: `1px solid ${catColor}25` }}>
            <span className="w-1.5 h-1.5 rounded-sm" style={{ background: catColor }} />
            {SOCIAL_CATEGORIES[post.category] || post.category}
          </span>
          {/* Sentiment */}
          <span className="font-mono-data text-[9px] px-1.5 py-0" style={{ color: sentimentColor, background: `${sentimentColor}12`, border: `1px solid ${sentimentColor}20` }}>
            {post.sentiment_label}
            {post.sentiment_score != null && ` (${post.sentiment_score > 0 ? '+' : ''}${post.sentiment_score})`}
          </span>
          {/* Timestamp */}
          {post.created_utc > 0 && (
            <span className="font-mono-data text-[9px] text-gray-400">
              {new Date(post.created_utc * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}
